// frontend/src/hooks/useSubscription.js
// Custom hook for subscription and usage data

import { useState, useEffect, useContext, createContext, useCallback } from 'react';
import axios from '../api/axiosConfig';
import { useAuth } from '../contexts/AuthContext';

const DEFAULT_USAGE_LIMITS = {
  voice: {
    minutesUsed: 0,
    minutesLimit: 600,
    percentUsed: 0
  },
  aiOperations: {
    used: 0,
    limit: 1000,
    percentUsed: 0
  },
  storage: {
    bytesUsed: 0,
    bytesLimit: 1073741824,
    percentUsed: 0
  }
};

const normalizeUsage = (usageData = {}) => {
  const voiceSource = usageData.voice || {};
  const aiSource = usageData.aiOperations || usageData.ai || {};
  const storageSource = usageData.storage || {};
  const sessionsSource = usageData.sessions || {};
  const bookmarksSource = usageData.bookmarks || {};

  const minutesUsed = voiceSource.minutesUsed ?? voiceSource.used ?? DEFAULT_USAGE_LIMITS.voice.minutesUsed;
  const minutesLimit = voiceSource.minutesLimit ?? voiceSource.limit ?? DEFAULT_USAGE_LIMITS.voice.minutesLimit;
  const voicePercent = voiceSource.percentUsed ?? DEFAULT_USAGE_LIMITS.voice.percentUsed;

  const aiUsed = aiSource.used ?? DEFAULT_USAGE_LIMITS.aiOperations.used;
  const aiLimit = aiSource.limit ?? DEFAULT_USAGE_LIMITS.aiOperations.limit;
  const aiPercent = aiSource.percentUsed ?? DEFAULT_USAGE_LIMITS.aiOperations.percentUsed;

  const storageUsed = storageSource.bytesUsed ?? storageSource.used ?? DEFAULT_USAGE_LIMITS.storage.bytesUsed;
  const storageLimit = storageSource.bytesLimit ?? storageSource.limit ?? DEFAULT_USAGE_LIMITS.storage.bytesLimit;
  const storagePercent = storageSource.percentUsed ?? DEFAULT_USAGE_LIMITS.storage.percentUsed;

  return {
    ...usageData,
    voice: {
      ...voiceSource,
      minutesUsed,
      used: minutesUsed,
      minutesLimit,
      limit: minutesLimit,
      percentUsed: voicePercent
    },
    aiOperations: {
      ...aiSource,
      used: aiUsed,
      limit: aiLimit,
      percentUsed: aiPercent
    },
    storage: {
      ...storageSource,
      bytesUsed: storageUsed,
      used: storageUsed,
      bytesLimit: storageLimit,
      limit: storageLimit,
      percentUsed: storagePercent
    },
    ai: {
      ...aiSource,
      used: aiUsed,
      limit: aiLimit,
      percentUsed: aiPercent
    },
    sessions: {
      count: sessionsSource.count ?? 0,
      // -1 from backend = unlimited, convert to Infinity for frontend
      countLimit: sessionsSource.countLimit === -1 ? Infinity : (sessionsSource.countLimit ?? 10),
      restored: sessionsSource.restored ?? 0,
      restoredLimit: sessionsSource.restoredLimit === -1 ? Infinity : (sessionsSource.restoredLimit ?? Infinity)
    },
    bookmarks: {
      count: bookmarksSource.count ?? 0,
      // -1 from backend = unlimited, convert to Infinity for frontend
      countLimit: bookmarksSource.countLimit === -1 ? Infinity : (bookmarksSource.countLimit ?? 1000)
    }
  };
};

const SubscriptionContext = createContext();

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdateTime, setLastUpdateTime] = useState(null);

  // Load subscription and usage data
  const loadData = useCallback(async (silent = false) => {
    if (!currentUser) return;

    try {
      if (!silent) setLoading(true);
      const token = await currentUser.getIdToken();

      const [planResponse, usageResponse] = await Promise.allSettled([
        axios.get('/api/billing/plan', {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get('/api/billing/usage', {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      // Handle plan response
      if (planResponse.status === 'fulfilled') {
        setSubscription(planResponse.value.data);
      } else {
        // Set default free plan if failed
        setSubscription({
          subscription: { plan: 'free', status: 'active' },
          planDetails: { voiceMinutes: 600, aiOps: 1000, storageGB: 1 },
          canUpgrade: true
        });
      }

      // Handle usage response
      if (usageResponse.status === 'fulfilled') {
        setUsage(normalizeUsage(usageResponse.value.data));
      } else {
        // Set default empty usage if failed
        setUsage(normalizeUsage());
      }
      
      setError(null);
      setLastUpdateTime(Date.now());
    } catch (err) {
      // This shouldn't happen with Promise.allSettled, but just in case
      console.warn('Subscription data load warning:', err);
      // Set defaults to prevent app from breaking
      setSubscription({
        subscription: { plan: 'free', status: 'active' },
        planDetails: { voiceMinutes: 600, aiOps: 1000, storageGB: 1 },
        canUpgrade: true
      });
      setUsage(normalizeUsage());
      setError(null); // Don't show error to user for billing issues
    } finally {
      if (!silent) setLoading(false);
    }
  }, [currentUser]);

  // Load data on mount and when user changes
  useEffect(() => {
    if (currentUser) {
      loadData();
    }
  }, [currentUser, loadData]);

  // Set up periodic refresh for usage data (every 30 seconds)
  useEffect(() => {
    if (!currentUser) return;
    
    const interval = setInterval(() => {
      loadData(true); // Silent refresh to avoid loading states
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [currentUser, loadData]);

  // Refresh data (for after upgrades, usage changes)
  const refresh = useCallback(async (silent = false) => {
    await loadData(silent);
  }, [loadData]);

  // Refresh usage only (lighter weight than full refresh)
  const refreshUsage = useCallback(async () => {
    if (!currentUser) return;

    try {
      const token = await currentUser.getIdToken();
      const response = await axios.get('/api/billing/usage', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setUsage(normalizeUsage(response.data));
      setLastUpdateTime(Date.now());
    } catch (err) {
      console.warn('Usage refresh failed:', err);
    }
  }, [currentUser]);

  // Check if user can use a feature
  const checkFeatureAccess = async (feature, options = {}) => {
    if (!currentUser) return { allowed: false, reason: 'not_authenticated' };

    try {
      const token = await currentUser.getIdToken();
      const response = await axios.post('/api/billing/check-feature', 
        { feature, options },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      return { allowed: true, usage: response.data.usage };
    } catch (err) {
      if (err.response?.status === 402) {
        return { 
          allowed: false, 
          upgradeRequired: true,
          ...err.response.data 
        };
      }
      
      return { 
        allowed: false, 
        reason: 'check_failed',
        error: err.response?.data?.error 
      };
    }
  };

  // Helper functions
  const isPro = () => subscription?.subscription?.plan === 'pro';
  const isFree = () => subscription?.subscription?.plan === 'free';
  const isPaid = () => {
    const plan = subscription?.subscription?.plan;
    return plan === 'starter' || plan === 'pro' || plan === 'max';
  };

  // Subscription status helpers
  const hasActiveSubscription = () => {
    const status = subscription?.subscription?.status;
    return status === 'active' || status === 'trialing';
  };

  const currentTier = () => {
    return subscription?.subscription?.plan || 'free';
  };

  const subscriptionStatus = () => {
    return subscription?.subscription?.status || 'inactive';
  };

  const isSubscribed = () => {
    const tier = currentTier();
    return tier !== 'free' && hasActiveSubscription();
  };

  const getUsagePercentage = (used, limit) => {
    if (!limit || limit === 'Infinity' || limit === Infinity) return 0;
    return Math.min(100, Math.round((used / limit) * 100));
  };

  const isNearLimit = (used, limit, threshold = 80) => {
    const percentage = getUsagePercentage(used, limit);
    return percentage >= threshold;
  };

  const formatStorage = (bytes) => {
    if (!bytes) return '0 B';
    const MB = 1024 * 1024;
    const GB = 1024 * MB;
    
    if (bytes < MB) return `${Math.round(bytes / 1024)} KB`;
    if (bytes < GB) return `${Math.round(bytes / MB)} MB`;
    return `${(bytes / GB).toFixed(1)} GB`;
  };

  const formatMinutes = (minutes) => {
    if (!minutes) return '0m';
    if (minutes < 60) return `${Math.round(minutes)}m`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = Math.round(minutes % 60);
    return remainingMinutes === 0 ? `${hours}h` : `${hours}h ${remainingMinutes}m`;
  };

  // Get tier limits for bulk upload and other features
  const getTierLimits = () => {
    // Import tier limits dynamically to avoid circular dependencies
    const TIER_LIMITS = {
      free: {
        bulkVoiceUpload: false,
        bulkMaxFilesPerBatch: 0,
        bulkMaxConcurrentUploads: 0,
        maxFileSizeBytes: 20 * 1024 * 1024,
        maxVoiceSecondsPerFile: 15 * 60,
      },
      starter: {
        bulkVoiceUpload: true,
        bulkMaxFilesPerBatch: 10,
        bulkMaxConcurrentUploads: 2,
        maxFileSizeBytes: 100 * 1024 * 1024,
        maxVoiceSecondsPerFile: 30 * 60,
      },
      pro: {
        bulkVoiceUpload: true,
        bulkMaxFilesPerBatch: 25,
        bulkMaxConcurrentUploads: 4,
        maxFileSizeBytes: 200 * 1024 * 1024,
        maxVoiceSecondsPerFile: 60 * 60,
      },
      max: {
        bulkVoiceUpload: true,
        bulkMaxFilesPerBatch: 50,
        bulkMaxConcurrentUploads: 6,
        maxFileSizeBytes: 1 * 1024 * 1024 * 1024,
        maxVoiceSecondsPerFile: 3 * 60 * 60,
      }
    };

    const tier = currentTier();
    return TIER_LIMITS[tier] || TIER_LIMITS.free;
  };

  const value = {
    subscription,
    usage,
    loading,
    error,
    refresh,
    refreshUsage,
    checkFeatureAccess,
    isPro,
    isFree,
    isPaid,
    isPaidUser: isPaid(), // Computed boolean for convenience
    hasActiveSubscription,
    currentTier,
    subscriptionStatus,
    isSubscribed,
    getUsagePercentage,
    isNearLimit,
    formatStorage,
    formatMinutes,
    getTierLimits,
    lastUpdateTime
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};